"use client";

import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/pro-solid-svg-icons/faCalendarDays";
import { faBolt } from "@fortawesome/pro-solid-svg-icons/faBolt";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { cn } from "@wondermaps/ui";
import type { VisitorEvent } from "../../../hooks/visitor/useVisitorEvents";
import {
  formatEventTime,
  formatEventSchedule,
  formatUntilTime,
  getRelativeDateLabel,
  getNextOccurrence,
} from "../../../lib/event-schedule";
import { analytics } from "../../../lib/analytics";
import { contrastText } from "../../../lib/color-utils";

interface EventsDisplayProps {
  events: VisitorEvent[];
  /** Compact mode for POI sheet (less padding, smaller text) */
  compact?: boolean;
  /** Brand accent color — overrides sponsor tier color on event badges */
  brandAccent?: string | null;
  /** Tap handler for "See all events at {poi}" — replaces static overflow text when provided */
  onSeeAllEvents?: () => void;
  /** POI name shown in the see-all button label */
  poiName?: string;
}

/**
 * Displays events for a POI in visitor view.
 * Shows happening now events prominently, then upcoming.
 */
export function EventsDisplay({ events, compact = false, brandAccent, onSeeAllEvents, poiName }: EventsDisplayProps) {
  const { t } = useTranslation("events");
  if (!events.length) {
    return null;
  }

  const happeningNow = events.filter((e) => e.status === "happening-now");
  const todayUpcoming = events.filter((e) => e.status === "today-upcoming");
  const futureUpcoming = events.filter((e) => e.status === "upcoming");

  return (
    <div className={cn("space-y-2", compact && "space-y-1.5")}>
      {/* Section header */}
      <div className="flex items-center gap-2">
        <FontAwesomeIcon icon={faCalendarDays}
          className={cn(
            "text-muted-foreground",
            compact ? "h-3.5 w-3.5" : "h-4 w-4"
          )}
          aria-hidden="true"
        />
        <span
          className={cn(
            "font-medium text-muted-foreground",
            compact ? "text-xs" : "text-sm"
          )}
        >
          {t("title")}
        </span>
        {happeningNow.length > 0 && (
          <span className="flex items-center gap-1 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
            <FontAwesomeIcon icon={faBolt} className="h-3 w-3" />
            {t("now")}
          </span>
        )}
      </div>

      {/* Happening Now */}
      {happeningNow.map((event) => (
        <EventItem key={event.id} event={event} compact={compact} isNow brandAccent={brandAccent} />
      ))}

      {/* Today Upcoming */}
      {todayUpcoming.map((event) => (
        <EventItem key={event.id} event={event} compact={compact} brandAccent={brandAccent} />
      ))}

      {/* Future Upcoming (show first 2 only in compact) */}
      {(compact ? futureUpcoming.slice(0, 2) : futureUpcoming).map((event) => (
        <EventItem key={event.id} event={event} compact={compact} brandAccent={brandAccent} />
      ))}

      {/* Show more indicator — button (deep-links to events tab filtered to this POI) or fallback text */}
      {compact && futureUpcoming.length > 2 && (
        onSeeAllEvents && poiName ? (
          <button
            type="button"
            onClick={onSeeAllEvents}
            className="w-full mt-1 inline-flex items-center justify-between text-xs text-primary hover:underline pl-5 pr-1 py-1"
          >
            <span>{t("seeAllAtPoi", { name: poiName })}</span>
            <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
          </button>
        ) : (
          <p className="text-xs text-muted-foreground pl-5">
            {t("moreUpcoming", { count: futureUpcoming.length - 2 })}
          </p>
        )
      )}
    </div>
  );
}

interface EventItemProps {
  event: VisitorEvent;
  compact?: boolean;
  isNow?: boolean;
  brandAccent?: string | null;
}

function EventItem({ event, compact, isNow, brandAccent }: EventItemProps) {
  const { t, i18n } = useTranslation("events");
  const locale = i18n.language;
  const nextOccurrence = getNextOccurrence(event);

  return (
    <div
      className={cn(
        "rounded-lg border",
        compact ? "p-2" : "p-3",
        isNow && "border-green-200 bg-green-50"
      )}
    >
      {/* Event name + sponsor badge + status */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className={cn("font-medium", compact ? "text-sm" : "text-base")}
          >
            {event.name}
          </span>
          {event.sponsorRef?.sponsorName && (
            <span
              className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold"
              style={(() => {
                const raw = brandAccent ?? event.sponsorRef?.sponsorTierColor ?? null;
                const isValidHex = raw && /^#[0-9A-Fa-f]{6}$/.test(raw);
                const bg = isValidHex ? raw : "#0d9488";
                return { backgroundColor: bg, color: contrastText(bg) };
              })()}
            >
              {event.sponsorRef.sponsorLogoUrl && (
                <img
                  src={event.sponsorRef.sponsorLogoUrl}
                  alt=""
                  className="h-3 w-3 rounded-sm object-contain"
                />
              )}
              {t("sponsoredBy", { name: event.sponsorRef.sponsorName })}
            </span>
          )}
        </div>
        {isNow && (
          <span className="shrink-0 px-1.5 py-0.5 bg-green-600 text-white rounded text-xs font-medium">
            {t("now")}
          </span>
        )}
      </div>

      {/* Time info */}
      <div
        className={cn(
          "text-muted-foreground mt-0.5",
          compact ? "text-xs" : "text-sm"
        )}
      >
        {isNow ? (
          formatUntilTime(event.endTime, t, locale)
        ) : event.status === "today-upcoming" ? (
          <>{t("todayAt", { time: formatEventTime(event, locale).split(" - ")[0] })}</>
        ) : nextOccurrence ? (
          <>
            {getRelativeDateLabel(nextOccurrence.date, undefined, t)} •{" "}
            {formatEventTime(event, locale)}
          </>
        ) : (
          formatEventSchedule(event, t)
        )}
      </div>

      {/* Multi-day schedule — show pattern + range when event recurs */}
      {event.scheduleType !== "single" && nextOccurrence && (
        <div
          className={cn(
            "text-muted-foreground/80 mt-0.5",
            compact ? "text-[11px]" : "text-xs"
          )}
        >
          {formatEventSchedule(event, t)}
        </div>
      )}

      {/* Headliner (if present) */}
      {event.headlinerName && (
        <div
          className={cn(
            "text-muted-foreground mt-1 flex items-center gap-1.5",
            compact ? "text-xs" : "text-sm"
          )}
        >
          {event.headlinerLogoUrl && (
            <img
              src={event.headlinerLogoUrl}
              alt=""
              className="h-4 w-4 rounded object-cover"
            />
          )}
          <span>{t("featuring", { name: event.headlinerName })}</span>
        </div>
      )}

      {/* Event Link */}
      {event.eventUrl && (
        <a
          href={event.eventUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => analytics.eventLinkClick({
            event_id: event.id,
            event_name: event.name,
            url: event.eventUrl!,
            poi_id: event.poiId ?? undefined,
          })}
          className={cn(
            "mt-1 inline-flex items-center gap-1 text-primary hover:underline",
            compact ? "text-xs" : "text-sm"
          )}
        >
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3" />
          <span>{event.eventUrlLabel || t("moreInfo", { defaultValue: "More Info" })}</span>
        </a>
      )}
    </div>
  );
}
